import { v4 as uuid } from "uuid";

import {
  issue__EventType,
  Participant,
} from "@medbillai/generated-graphql-types";

import type {
  BaseEvent,
  ChatEvent,
  DocumentUploadedEvent,
  UserSelectionRequestedEvent,
} from "./types";

type EventOptions = {
  sentAt?: string;
};

const createSentByProfile = (participant: Participant) => ({
  __typename: "user__Profile_Type" as const,
  name: participant === Participant.USER ? "You" : null,
});

type BaseEventFields = Pick<
  BaseEvent,
  | "id"
  | "eventType"
  | "participant"
  | "sentAt"
  | "userVisible"
  | "eventFeedbacks"
  | "sentByProfile"
>;

const createBaseEvent = <T extends BaseEvent["__typename"]>({
  __typename,
  eventType,
  participant,
  sentAt,
}: {
  __typename: T;
  eventType: issue__EventType;
  participant: Participant;
  sentAt?: string;
}): BaseEventFields & { __typename: T } => ({
  __typename,
  id: uuid().toString(),
  eventType,
  participant,
  sentAt: sentAt ?? new Date().toISOString(),
  userVisible: true,
  eventFeedbacks: [],
  sentByProfile: createSentByProfile(participant),
});

export function createUserChatEvent(
  message: string,
  options: EventOptions = {},
): ChatEvent {
  return {
    ...createBaseEvent({
      __typename: "issue__ChatEvent_Type",
      eventType: issue__EventType.CHAT,
      participant: Participant.USER,
      sentAt: options.sentAt,
    }),
    chatBody: message,
  };
}

export function createSystemMessageEvent(
  message: string,
  options?: string[],
  eventOptions: EventOptions = {},
): UserSelectionRequestedEvent | ChatEvent {
  if (options && options.length > 0) {
    return {
      ...createBaseEvent({
        __typename: "issue__UserSelectionRequestedEvent_Type",
        eventType: issue__EventType.USER_SELECTION_REQUESTED,
        participant: Participant.SERVICE,
        sentAt: eventOptions.sentAt,
      }),
      stringMessage: message,
      stringOptions: options,
    };
  }

  return {
    ...createBaseEvent({
      __typename: "issue__ChatEvent_Type",
      eventType: issue__EventType.CHAT,
      participant: Participant.SERVICE,
      sentAt: eventOptions.sentAt,
    }),
    chatBody: message,
  };
}

export function createDocumentUploadEvent(
  {
    id,
    documentId,
    uploading,
    sentAt,
    previewPromptMessage,
  }: {
    id?: string;
    documentId?: string;
    uploading?: boolean;
    sentAt?: string;
    // When set, the card renders this message (streamed) above the upload CTA —
    // used by the BILL_ISSUE_V2 preview so the prompt + action are one event.
    previewPromptMessage?: string;
  } = {
    uploading: false,
  },
): DocumentUploadedEvent {
  return {
    ...createBaseEvent({
      __typename: "issue__DocumentUploadedEvent_Type",
      eventType: issue__EventType.DOCUMENT_UPLOADED,
      participant: Participant.USER,
      sentAt,
    }),
    isClusterEnd: false,
    ...(id ? { id } : {}),
    ...(previewPromptMessage ? { previewPromptMessage } : {}),
    uploading,
    uploadFailed: false,
    document: {
      __typename: "document__Document_Type" as const,
      id: documentId ?? uuid().toString(),
      name: "document.pdf",
      createdAt: new Date().toISOString(),
      files: [],
      dataType: null,
      latestIngestion: null,
    },
  };
}
